import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Field, inputCls } from "@/components/ui-primitives";

const emptyForm = {
  location_id: "",
  type: "",
  asset_id: "",
  serial: "",
  last_service: "",
};

export default function Equipment() {
  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [meta, setMeta] = useState({ equipment_types: [] });
  const [form, setForm] = useState(emptyForm);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterLoc, setFilterLoc] = useState("all");

  const load = async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (filterType !== "all") params.type = filterType;
      if (filterLoc !== "all") params.location_id = filterLoc;
      const [eqRes, locRes, metaRes] = await Promise.all([
        api.get("/equipment", { params }),
        api.get("/locations"),
        api.get("/meta"),
      ]);
      setItems(eqRes.data);
      setLocations(locRes.data);
      setMeta(metaRes.data);
      setForm((f) => ({
        ...f,
        location_id: f.location_id || locRes.data[0]?.id || "",
        type: f.type || metaRes.data.equipment_types[0] || "",
      }));
    } catch {
      toast.error("Failed to load");
    }
  };

  useEffect(() => {
    const t = setTimeout(load, 250); // debounce search
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search, filterType, filterLoc]);

  const submit = async (e) => {
    e.preventDefault();
    if (locations.length === 0) { toast.error("Add a location first"); return; }
    if (!form.asset_id.trim()) { toast.error("Asset ID is required"); return; }
    try {
      const payload = { ...form };
      if (!payload.last_service) delete payload.last_service;
      await api.post("/equipment", payload);
      toast.success("Equipment added");
      setForm({ ...emptyForm, location_id: locations[0]?.id || "", type: meta.equipment_types[0] || "" });
      load();
    } catch { toast.error("Add failed"); }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? All PM records and faults will be removed.`)) return;
    try {
      await api.delete(`/equipment/${id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="p-8 max-w-[1800px] mx-auto">
      <PageHeader eyebrow="Inventory" title="Equipment" accent="Roster" />

      <form onSubmit={submit} className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 bg-[#121212] border border-white/10 p-5 mb-6">
        <Field label="Location *">
          <select data-testid="eq-location" value={form.location_id} onChange={(e) => setForm({ ...form, location_id: e.target.value })} className={inputCls}>
            {locations.length === 0 && <option value="">— No locations —</option>}
            {locations.map((l) => (<option key={l.id} value={l.id}>{l.name}</option>))}
          </select>
        </Field>
        <Field label="Type *">
          <select data-testid="eq-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={inputCls}>
            {meta.equipment_types.map((t) => (<option key={t} value={t}>{t}</option>))}
          </select>
        </Field>
        <Field label="Asset ID / Bay # *">
          <input data-testid="eq-assetid" value={form.asset_id} onChange={(e) => setForm({ ...form, asset_id: e.target.value })} className={inputCls} />
        </Field>
        <Field label="Serial Number">
          <input data-testid="eq-serial" value={form.serial} onChange={(e) => setForm({ ...form, serial: e.target.value })} className={inputCls} />
        </Field>
        <Field label="Last Service">
          <input type="date" data-testid="eq-lastservice" value={form.last_service || ""} onChange={(e) => setForm({ ...form, last_service: e.target.value })} className={inputCls} />
        </Field>
        <div className="flex items-end lg:col-span-5">
          <button data-testid="eq-add-btn" type="submit" className="w-full md:w-auto px-6 py-2.5 bg-[#007AFF] hover:bg-[#3395FF] text-white text-sm font-medium">
            Add Equipment
          </button>
        </div>
      </form>

      {/* Search & Filter bar */}
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#737373]" />
          <input
            data-testid="eq-search"
            placeholder="Search asset ID, serial, type, location..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-transparent border border-white/20 pl-10 pr-3 py-2 text-sm text-white placeholder:text-[#737373] focus:outline-none focus:border-[#007AFF] focus:ring-1 focus:ring-[#007AFF]"
          />
        </div>
        <select
          data-testid="eq-filter-type"
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="bg-[#0A0A0A] border border-white/20 px-3 py-2 text-sm text-white focus:outline-none focus:border-[#007AFF]"
        >
          <option value="all">All Types</option>
          {meta.equipment_types.map((t) => (<option key={t} value={t}>{t}</option>))}
        </select>
        <select
          data-testid="eq-filter-location"
          value={filterLoc}
          onChange={(e) => setFilterLoc(e.target.value)}
          className="bg-[#0A0A0A] border border-white/20 px-3 py-2 text-sm text-white focus:outline-none focus:border-[#007AFF]"
        >
          <option value="all">All Locations</option>
          {locations.map((l) => (<option key={l.id} value={l.id}>{l.name}</option>))}
        </select>
        <div className="flex items-center font-mono-tab text-[10px] tracking-[0.2em] uppercase text-[#737373]">
          {items.length} result{items.length === 1 ? "" : "s"}
        </div>
      </div>

      <div className="border border-white/10 bg-[#121212] overflow-x-auto">
        <table data-testid="eq-table" className="w-full text-sm">
          <thead className="bg-black/60 backdrop-blur-xl">
            <tr className="text-left font-mono-tab text-[10px] tracking-[0.2em] uppercase text-[#737373]">
              <th className="px-4 py-3 border-b border-white/10">Location</th>
              <th className="px-4 py-3 border-b border-white/10">Type</th>
              <th className="px-4 py-3 border-b border-white/10">Asset ID</th>
              <th className="px-4 py-3 border-b border-white/10">Serial</th>
              <th className="px-4 py-3 border-b border-white/10">Last Service</th>
              <th className="px-4 py-3 border-b border-white/10 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (<tr><td colSpan={6} className="px-4 py-12 text-center text-[#A3A3A3]">No equipment matches.</td></tr>)}
            {items.map((e) => (
              <tr key={e.id} className="border-b border-white/5 hover:bg-white/[0.03]">
                <td className="px-4 py-3 text-[#E0E0E0]">{e.location_name || "—"}</td>
                <td className="px-4 py-3 text-white">{e.type}</td>
                <td className="px-4 py-3 font-mono-tab text-xs text-[#E0E0E0]">{e.asset_id}</td>
                <td className="px-4 py-3 font-mono-tab text-xs text-[#A3A3A3]">{e.serial || "—"}</td>
                <td className="px-4 py-3 font-mono-tab text-xs text-[#E0E0E0]">{e.last_service || "—"}</td>
                <td className="px-4 py-3">
                  <div className="flex justify-end">
                    <button data-testid={`eq-del-${e.id}`} onClick={() => remove(e.id, `${e.type} ${e.asset_id}`)} className="p-1.5 border border-white/10 hover:border-[#FF3B30]/40 hover:bg-[#FF3B30]/10">
                      <Trash2 className="w-3.5 h-3.5 text-[#FF3B30]" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
